import { useEffect, useState } from "react";
import { Instagram, Facebook, MessageCircle as WhatsappIcon } from "lucide-react";
import InfoPage from "../components/InfoPage";
import api from "../lib/api";

export default function About() {
    const [social, setSocial] = useState({ instagram: "", facebook: "", whatsapp: "" });

    useEffect(() => {
        api.get("/app-settings")
            .then(({ data }) => setSocial(data.social_links || {}))
            .catch(() => {});
    }, []);

    const hasAnySocial = !!(social.instagram || social.facebook || social.whatsapp);

    return (
        <InfoPage
            title="About Us"
            subtitle="Founded in 2019, New Delhi"
            testid="about-page"
            footer={
                <div className="flex items-center justify-between gap-4 flex-wrap" data-testid="about-allsports-footer">
                    <div className="text-[11px] text-slate-500 leading-relaxed">
                        Drive or Kick is a brand operated by ALL SPORTS.<br />
                        © 2026 ALL SPORTS. All Rights Reserved.
                    </div>
                    {hasAnySocial && (
                        <div className="flex items-center gap-3" data-testid="about-social-icons">
                            {social.instagram && (
                                <a href={social.instagram} target="_blank" rel="noopener noreferrer"
                                    data-testid="social-instagram"
                                    aria-label="Instagram"
                                    className="text-slate-400 hover:text-[#E1306C] transition-colors">
                                    <Instagram className="w-5 h-5" />
                                </a>
                            )}
                            {social.facebook && (
                                <a href={social.facebook} target="_blank" rel="noopener noreferrer"
                                    data-testid="social-facebook"
                                    aria-label="Facebook"
                                    className="text-slate-400 hover:text-[#1877F2] transition-colors">
                                    <Facebook className="w-5 h-5" />
                                </a>
                            )}
                            {social.whatsapp && (
                                <a href={social.whatsapp} target="_blank" rel="noopener noreferrer"
                                    data-testid="social-whatsapp"
                                    aria-label="WhatsApp"
                                    className="text-slate-400 hover:text-[#25D366] transition-colors">
                                    <WhatsappIcon className="w-5 h-5" />
                                </a>
                            )}
                        </div>
                    )}
                </div>
            }
        >
            <p>
                Founded in 2019, <b className="text-white">Drive or Kick</b> was born with a simple but powerful mission — to give students, youth, and sports enthusiasts access to quality turf facilities, right in their neighborhood in New Delhi.
            </p>
            <p className="mt-4">
                We are a multi-sport turf platform operating within school premises or in an enclosed utilised space, making it easier for people to book and play without the hassle of finding a good ground.
            </p>

            <H2>Our Mission</H2>
            <p>
                To provide affordable, accessible, and state-of-the-art sports facilities for everyone — from casual players to competitive athletes.
            </p>

            <H2>Our Vision</H2>
            <p>
                To build a sporting community where students, corporates, and sports lovers come together through the love of the game.
            </p>

            <H2>What We Offer</H2>
            <ul className="mt-2 space-y-1.5">
                <li>🏟️ <b className="text-white">Turf Booking</b> — Book your slot easily through the app</li>
                <li>🎓 <b className="text-white">Academy</b> — Structured coaching and training programs for students</li>
                <li>🏆 <b className="text-white">Tournaments</b> — Competitive events for all skill levels</li>
                <li>🤝 <b className="text-white">Corporate Events</b> — Team-building and sporting events for companies</li>
                <li>⚽ <b className="text-white">Community Activities</b> — Group games, leagues, and community sports</li>
            </ul>

            <H2>Why Drive or Kick</H2>
            <ul className="mt-2 space-y-1.5 list-disc list-inside">
                <li>Located within school premises for a safe and clean environment</li>
                <li>Easy online booking and advance payment system</li>
                <li>Loyalty rewards for regular players</li>
                <li>Transparent cancellation and refund policy</li>
                <li>Trusted by players across New Delhi</li>
            </ul>
        </InfoPage>
    );
}

function H2({ children }) {
    return <h2 className="font-display text-lg font-bold uppercase text-white mt-6 mb-1 tracking-widest">{children}</h2>;
}
